import { IApiAxiosError, bulkImportTopics } from "@/api";
import { IModalAction } from "@/components/shared/Modal";
import { Stack, Typography } from "@mui/material";
import { FormContainer } from "@rhf-kit/mui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { PreprocessForm } from "./PreprocessForm";
import { PreprocessResult } from "./PreprocessResult";

interface IFormData {
    file?: File;
    objectName?: string;
}

interface IProps {
    onClose: () => void;
    onTitleChange?: (title: string) => void;
    onSubtitleChange?: (subtitle: string | undefined) => void;
    onActionsChange?: (actions: IModalAction[]) => void;
    onLoadingChange?: (loading: boolean) => void;
    onFooterTextChange?: (footerText: ReactNode) => void;
}

/**
 * PreprocessStep component - Orchestrates the topic import flow
 */
export const PreprocessStep = ({
    onClose,
    onTitleChange,
    onSubtitleChange,
    onActionsChange,
    onLoadingChange,
    onFooterTextChange,
}: IProps) => {
    const [hasFile, setHasFile] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [previewData, setPreviewData] = useState<Awaited<
        ReturnType<typeof bulkImportTopics>
    > | null>(null);
    const submitRef = useRef<HTMLButtonElement>(null);

    const queryClient = useQueryClient();
    const {
        mutate: preprocessTopics,
        isPending,
        isError,
        error,
    } = useMutation({
        mutationFn: ({
            objectName,
            preprocessOnly,
        }: {
            objectName: string;
            preprocessOnly: boolean;
        }) => bulkImportTopics(objectName, preprocessOnly),
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({
                queryKey: ["admin-topics"],
            });
            if (variables.preprocessOnly) {
                // Show preview after preprocessing
                setPreviewData(data);
                setShowPreview(true);
            } else {
                // Close on successful import
                onClose();
            }
        },
    });

    const defaultValues: IFormData = {
        file: undefined,
        objectName: undefined,
    };

    // Update loading state
    useEffect(() => {
        onLoadingChange?.(isPending);
    }, [isPending, onLoadingChange]);

    // Update footer text for errors
    useEffect(() => {
        if (isError && error) {
            const axiosError = error as IApiAxiosError;
            const errorMessage =
                axiosError.response?.data?.detail ||
                "An error occurred while importing topics.";
            onFooterTextChange?.(
                <Typography variant="caption" color="error">
                    {errorMessage}
                </Typography>
            );
        } else {
            onFooterTextChange?.(undefined);
        }
    }, [isError, error, onFooterTextChange]);

    // Update title and subtitle based on current state
    useEffect(() => {
        if (showPreview) {
            onTitleChange?.("Bulk Import Preview");
            onSubtitleChange?.(
                "Verify the results below and click Import to proceed."
            );
        } else {
            onTitleChange?.("Bulk Import Topics");
            onSubtitleChange?.("Upload an Excel file to import topics");
        }
    }, [showPreview, onTitleChange, onSubtitleChange]);

    const handleSubmit = useCallback(() => {
        submitRef.current?.click();
    }, []);

    // Update actions based on current state
    useEffect(() => {
        if (showPreview) {
            // Preview step - show Import button
            onActionsChange?.([
                {
                    label: "Import",
                    onClick: handleSubmit,
                    variant: "contained",
                    color: "primary",
                    disabled: isPending,
                },
            ]);
        } else {
            // Form step - show Continue button
            onActionsChange?.([
                {
                    label: "Continue",
                    onClick: handleSubmit,
                    variant: "contained",
                    color: "primary",
                    disabled: !hasFile || isPending,
                },
            ]);
        }
    }, [isPending, showPreview, hasFile, handleSubmit, onActionsChange]);

    const handleSuccess = (formData: IFormData) => {
        if (!formData.objectName) return;

        preprocessTopics({
            objectName: formData.objectName,
            preprocessOnly: !showPreview,
        });
    };

    return (
        <FormContainer defaultValues={defaultValues} onSuccess={handleSuccess}>
            {/* Hidden submit button triggered by Modal actions */}
            <button ref={submitRef} type="submit" style={{ display: "none" }} />

            <Stack gap={2}>
                {!showPreview && <PreprocessForm onFileChange={setHasFile} />}

                {showPreview && previewData && (
                    <PreprocessResult results={previewData} />
                )}
            </Stack>
        </FormContainer>
    );
};
